import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/providers/AuthProvider';

export type SubscriptionPlan = {
  id: string;
  name: string;
  displayName: string;
  description: string;
  tier: 'free' | 'professional' | 'scale' | 'agencyPro';
  billing: {
    monthly: {
      price: number;
      stripePriceId: string;
    };
    yearly: {
      price: number;
      stripePriceId: string;
      discount: number;
    };
    setup?: {
      fee: number;
      stripePriceId: string;
    };
  };
  features: {
    campaigns: {
      maxActive: number;
      maxTotal: number;
      aiOptimization: boolean;
      advancedTargeting: boolean;
    };
    emails: {
      maxSubscribers: number;
      maxSendsPerMonth: number;
      aiGeneration: boolean;
      automations: boolean;
      customTemplates: boolean;
    };
    api: {
      maxCallsPerMonth: number;
      rateLimitPerSecond: number;
      webhooks: boolean;
      customIntegrations: boolean;
    };
    analytics: {
      dataRetentionMonths: number;
      advancedReports: boolean;
      realTimeData: boolean;
      customDashboards: boolean;
      exportData: boolean;
    };
    support: {
      level: 'basic' | 'priority' | 'dedicated';
      aiSupport: boolean;
      phoneSupport: boolean;
      chatSupport: boolean;
      emailSupport: boolean;
      responseTime: number;
      sla: number;
    };
    security: {
      sso: boolean;
      mfa: boolean;
      auditLogs: boolean;
    };
    integrations: {
      maxConnections: number;
      premiumConnectors: boolean;
      customConnectors: boolean;
    };
    whiteLabel: {
      enabled: boolean;
      customBranding: boolean;
      customDomain: boolean;
      removeAdvoltixBranding: boolean;
      brandedEmails: boolean;
    };
  };
  limits: {
    maxClients: number;
    campaigns: number;
    emailCredits: number;
    apiCalls: number;
    aiTokens: number;
    usage: {
      maxAdSpendPerMonth: number;
      maxEmailSendsPerMonth: number;
      maxApiCallsPerMonth: number;
      maxCampaignsActive: number;
      maxEmailLists: number;
      maxCreativeAssets: number;
    };
    quotas: {
      resetPeriod: 'monthly' | 'weekly' | 'daily';
      overage: {
        allowed: boolean;
        rate: number;
      };
    };
  };
  metadata: Record<string, unknown>;
};

export type SubscriptionPlansResponse = {
  plans: SubscriptionPlan[];
};

export function useSubscriptionPlans() {
  const { token } = useAuth();
  
  return useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      try {
        console.log('[useSubscriptionPlans] Fetching plans from /api/v1/subscription-management/plans');
        const response = await apiClient.get<{ success: boolean; data: { plans: SubscriptionPlan[] } }>(
          '/api/v1/subscription-management/plans',
          token
        );
        
        console.log('[useSubscriptionPlans] Response received:', { 
          success: response.success, 
          plansCount: response.data?.plans?.length 
        });
        
        if (!response.success || !response.data?.plans) {
          throw new Error('Invalid response format from plans endpoint');
        }
        
        // Parse prices from strings to numbers (backend returns prices as strings)
        const plans = response.data.plans.map(plan => ({
          ...plan,
          billing: {
            ...plan.billing,
            monthly: {
              ...plan.billing.monthly,
              price: typeof plan.billing.monthly.price === 'string' 
                ? Number(plan.billing.monthly.price) 
                : plan.billing.monthly.price,
            },
            yearly: {
              ...plan.billing.yearly,
              price: typeof plan.billing.yearly.price === 'string'
                ? Number(plan.billing.yearly.price)
                : plan.billing.yearly.price,
            },
          },
        }));
        
        const sortedPlans = [...plans].sort((a, b) => {
          const orderA = Number((a.metadata as { displayOrder?: number })?.displayOrder ?? 999);
          const orderB = Number((b.metadata as { displayOrder?: number })?.displayOrder ?? 999);
          return orderA - orderB;
        });
        
        console.log('[useSubscriptionPlans] Returning sorted plans:', sortedPlans.length);
        return sortedPlans;
      } catch (error) {
        console.error('[useSubscriptionPlans] Error fetching plans:', error);
        throw error;
      }
    },
    // Allow fetching without authentication for landing page
    enabled: true,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useChangePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variables: { planId: string; token: string | null }) => {
      // Was posting to the legacy /api/v1/subscription/change-plan, which
      // validates against the old 4-tier enum (rejects 'scale'/'agencyPro'
      // outright) and, for the one string that collides with a new plan id
      // ('professional'), silently updates the disconnected legacy
      // userUsageLimits/users.status system instead of the real Stripe
      // subscription — the customer sees "Plan updated" while their actual
      // billing never changes. This is the real, Stripe-backed endpoint.
      const response = await apiClient.put<{
        success: boolean;
        data: { subscription: { id: string; planId: string; status: string }; prorationAmount: number; stripeSubscriptionId: string };
      }>(
        '/api/v1/subscription-management/subscription/change-plan',
        { planId: variables.planId, prorationBehavior: 'create_prorations' },
        variables.token
      );
      return { success: response.success, newPlan: response.data?.subscription?.planId };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['billing-overview'] });
      void queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
      void queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
    },
  });
}

/** Add-on package from API (public pricing page) */
export type AddonPackage = {
  id: string;
  name: string;
  displayName: string;
  description: string;
  category: string;
  type: 'usage' | 'feature' | 'service';
  price: string;
  stripePriceId: string;
  quantity: number;
  unit: string;
  metadata: Record<string, unknown>;
};

export function useAddOns() {
  return useQuery({
    queryKey: ['add-ons'],
    queryFn: async (): Promise<AddonPackage[]> => {
      try {
        const response = await apiClient.get<{
          success?: boolean;
          data?: { addOns?: AddonPackage[] };
        }>('/api/v1/subscription-management/add-ons');
        const addOns = response?.data?.addOns;
        if (Array.isArray(addOns) && addOns.length > 0) {
          return addOns;
        }
        return [];
      } catch {
        return [];
      }
    },
    enabled: true,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

export function useCheckoutAddon() {
  const { token } = useAuth();
  return useMutation({
    mutationFn: async (addonId: string) => {
      const response = await apiClient.post<{
        success: boolean;
        data: { url: string; sessionId: string };
      }>('/api/v1/subscription-management/checkout/addon', { addonId }, token);
      if (!response.success || !response.data?.url) {
        throw new Error('Failed to create checkout session');
      }
      return response.data.url;
    },
  });
}

export type CheckoutPlanParams = { planId: string; billingCycle: 'monthly' | 'yearly' };

export function useCheckoutPlan() {
  const { token } = useAuth();
  return useMutation({
    mutationFn: async (params: CheckoutPlanParams) => {
      const response = await apiClient.post<{
        success: boolean;
        data: { url: string; sessionId: string };
      }>('/api/v1/subscription-management/checkout/plan', params, token);
      if (!response.success || !response.data?.url) {
        throw new Error('Failed to create checkout session');
      }
      return response.data.url;
    },
  });
}

export interface TrialStatus {
  isTrialing:     boolean;
  trialStart:     string | null;
  trialEnd:       string | null;
  daysRemaining:  number;
  hoursRemaining: number;
  expired:        boolean;
  trialDays:      number;
  planId:         string;
}

export function useTrialStatus() {
  const { token } = useAuth();

  return useQuery({
    queryKey: ['trial-status'],
    queryFn: () => apiClient.get<TrialStatus>('/api/v1/trial/status', token),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Check if current organization has access to white label features
 */
export function useWhiteLabelAccess() {
  const { token } = useAuth();

  return useQuery({
    queryKey: ['white-label-access'],
    queryFn: async (): Promise<{ hasAccess: boolean; planId: string }> => {
      try {
        // Was the legacy /api/v1/subscription/check-access, backed by a
        // second feature-gate system with its own bugs (see backend
        // lib/feature-gates.ts removal) that denied every feature to every
        // org. This is the real, trial-aware feature gate.
        const response = await apiClient.get<{ success: boolean; data: { allowed: boolean; reason?: string } }>(
          '/api/v1/subscription-management/feature-access/whiteLabel',
          token
        );
        return { hasAccess: response?.data?.allowed ?? false, planId: 'free' };
      } catch (_error) {
        // If the endpoint doesn't exist yet, assume no access
        return { hasAccess: false, planId: 'free' };
      }
    },
    enabled: !!token, // Only fetch if we have a token
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

